import {CSSProperties, ReactNode, useContext, useEffect, useMemo, useState} from 'react';
import {Layer, MapRef, Source} from 'react-map-gl';
import mapboxgl from 'mapbox-gl';
import BaseMap from '../../../../../components/base-map/base-map';
import {NetworkContext} from '../../../lib/context';
import {observer} from 'mobx-react';
import {safeConsoleError} from '../../../../../lib/sanitize';
import {CountyFillPaintFactory} from './boundary-layer-strategies';
import {IAdequacyByCountyTableRow} from '../types';

interface IMapContainerProps {
    className?: string;
    title: string;
    subtitle?: string;
    mapStyle?: CSSProperties,
    renderMap?: boolean;
    onRenderDone?: () => void;
    id: string;
    specialtyCode?: string; // Optional: display data for specific specialty code
    showBoundaries?: boolean; // Optional: show county boundary layers derived from store
    countyFillPaintFactory?: CountyFillPaintFactory; // Optional: strategy for county fill layer paint
    countyData?: IAdequacyByCountyTableRow[] | null; // Optional: per-specialty adequacy data for fill strategies
    legend?: ReactNode; // Optional: legend overlay rendered inside the map
}

const PROVIDER_COLOR = 'rgba(255, 222, 102, 1)';
const CONTINENTAL_US_BOUNDS: [number, number, number, number] = [-124.7844079, 24.7433195, -66.9513812, 49.3457868];

const MapContainer = observer((props: IMapContainerProps) => {
    const {title, subtitle, mapStyle, renderMap = true, onRenderDone, id, specialtyCode, showBoundaries = true, countyFillPaintFactory, countyData, legend} = props;

    const resolvedClassName = useMemo(() => {
        const base = 'map-container';
        return props.className ? `${base} ${props.className}` : base;
    }, [props.className]);

    const networkStore = useContext(NetworkContext);
    const [isMapActive, setIsMapActive] = useState(renderMap);
    const [hasRendered, setHasRendered] = useState(false);
    const [mapRef, setMapRef] = useState<MapRef | null>(null);
    const [imgSrc, setImgSrc] = useState<string | undefined>();
    const [hasError, setHasError] = useState(false);
    const [isMapPinLoaded, setIsMapPinLoaded] = useState(false);

    const providerDataBySpecialty = useMemo(
        () => (networkStore.reportProviderGeoJson || {}) as Record<string, any>,
        [networkStore.reportProviderGeoJson]
    );

    const stateMapboxIds = useMemo(
        () => networkStore.getGeoFilterUniqueValuesByKey('GEOGRAPHY', 'state_additional_info.mapbox_id'),
        [networkStore.availableGeographyFilters]
    );

    const countyMapboxIds = useMemo(
        () => networkStore.getGeoFilterUniqueValuesByKey('GEOGRAPHY', 'county_additional_info.mapbox_id'),
        [networkStore.availableGeographyFilters]
    );

    const countyBboxes = useMemo(
        () => networkStore.getGeoFilterUniqueValuesByKey('GEOGRAPHY', 'county_bbox'),
        [networkStore.availableGeographyFilters]
    );

    const stateFilter = useMemo(
        () => ['match', ['get', 'mapbox_id'], stateMapboxIds, true, false] as mapboxgl.FilterSpecification,
        [stateMapboxIds]
    );

    const countyFilter = useMemo(
        () => [
            'all',
            ['match', ['get', 'worldview'], ['all', 'US'], true, false],
            ['match', ['get', 'mapbox_id'], countyMapboxIds, true, false]
        ] as mapboxgl.FilterSpecification,
        [countyMapboxIds]
    );

    const countyFillPaint = useMemo(() => {
        if (!countyFillPaintFactory) return null;
        if (countyData === null) return null;
        return countyFillPaintFactory(networkStore, countyData ?? undefined);
    }, [countyFillPaintFactory, countyData, networkStore.availableGeographyFilters]);

    const mapBounds = useMemo((): [number, number, number, number] => {
        if (!countyBboxes || countyBboxes.length === 0) return CONTINENTAL_US_BOUNDS;
        const bounds = new mapboxgl.LngLatBounds();
        for (const bboxStr of countyBboxes) {
            const parsed = JSON.parse(bboxStr) as [number, number, number, number];
            bounds.extend([parsed[0], parsed[1]] as mapboxgl.LngLatLike);
            bounds.extend([parsed[2], parsed[3]] as mapboxgl.LngLatLike);
        }
        const sw = bounds.getSouthWest();
        const ne = bounds.getNorthEast();
        return [sw.lng, sw.lat, ne.lng, ne.lat];
    }, [countyBboxes]);

    const providerGeoJson = useMemo(
        () => specialtyCode ? providerDataBySpecialty[specialtyCode] : undefined,
        [specialtyCode, providerDataBySpecialty]
    );

    const providerFeatureCount = providerGeoJson?.features?.length ?? 0;

    const boundariesReady = !showBoundaries || stateMapboxIds.length > 0 || countyMapboxIds.length > 0;

    // Sync isMapActive with renderMap prop changes
    useEffect(() => {
        if (renderMap && !isMapActive && !imgSrc && !hasError) {
            setIsMapActive(true);
        }
    }, [renderMap, isMapActive, imgSrc, hasError]);

    // Effect to wait for map-pin image to load before rendering provider pins
    useEffect(() => {
        const map = mapRef?.getMap();
        if (!map) return;

        // If we're rendering a specialty-specific map
        if (specialtyCode) {
            // Data is still loading for this specialty - don't mark as ready yet
            if (providerGeoJson === undefined) {
                setIsMapPinLoaded(false);
                return;
            }
            // Data loaded but no features - no pins needed, mark as ready
            if (providerFeatureCount === 0) {
                setIsMapPinLoaded(true);
                return;
            }
            // Data loaded with features - need to wait for map-pin image
        } else {
            // No specialty code means no provider pins will be rendered
            setIsMapPinLoaded(true);
            return;
        }

        const checkMapPinLoaded = () => {
            if (map.hasImage('map-pin')) {
                setIsMapPinLoaded(true);
            } else {
                // Check again on next idle cycle
                map.once('idle', checkMapPinLoaded);
            }
        };

        // Also listen for styleimagemissing event to catch and recover from missing image errors
        const onStyleImageMissing = (e: any) => {
            if (e.id === 'map-pin') {
                safeConsoleError('map-pin image missing, waiting for load...');
                // The BaseMap component should be loading it via styleimagemissing handler
                // We'll wait for next idle to check again
                map.once('idle', checkMapPinLoaded);
            }
        };

        checkMapPinLoaded();
        map.on('styleimagemissing', onStyleImageMissing);

        return () => {
            map.off('styleimagemissing', onStyleImageMissing);
        };
    }, [mapRef, specialtyCode, providerGeoJson, providerFeatureCount]);

    useEffect(() => {
        const map = mapRef?.getMap();
        if (!map) return;

        // Wait until boundary data is ready
        if (!boundariesReady) return;
        // Wait for county fill paint when a factory is provided but paint hasn't resolved yet.
        if (countyFillPaintFactory && !countyFillPaint) return;
        // If rendering a specialty-specific map, wait for the data to load
        if (specialtyCode && providerGeoJson === undefined) return;
        // Wait for map-pin image to load if we have provider data
        if (providerFeatureCount > 0 && !isMapPinLoaded) return;

        // Fit the map viewport to the computed state bounds (or continental US fallback)
        map.fitBounds(mapBounds, {padding: 40});

        const onIdle = () => {
            try {
                // All assets should be ready at this point (boundaries, images, layers, data)
                const canvas = map.getCanvas();
                const imgUrl = canvas.toDataURL('png');
                setImgSrc(imgUrl);
                // Only call onRenderDone once per map instance
                if (!hasRendered) {
                    onRenderDone?.();
                    setHasRendered(true);
                }
                setIsMapActive(false);
            } catch (error) {
                safeConsoleError('Error capturing map canvas:', error);
                setHasError(true);
                // Only call onRenderDone once per map instance
                if (!hasRendered) {
                    onRenderDone?.();
                    setHasRendered(true);
                }
                setIsMapActive(false);
            }
        };

        const onError = (e: any) => {
            const msg = e?.error?.message || e?.message || '';
            if (msg.includes('WebGL') || msg.includes('context lost')) {
                safeConsoleError('Map fatal error:', msg);
                setHasError(true);
                onRenderDone?.();
                setIsMapActive(false);
            } else {
                safeConsoleError('Map non-fatal error:', msg);
            }
        };

        map.once('idle', onIdle);

        map.on('error', onError);

        return () => {
            map.off('idle', onIdle);
            map.off('error', onError);
        };
    }, [mapRef, boundariesReady, countyFillPaintFactory, countyFillPaint, specialtyCode, mapBounds, providerGeoJson, providerFeatureCount, isMapPinLoaded]);

    const hasProviderData = Object.keys(providerDataBySpecialty).length > 0;
    const shouldRenderMap = isMapActive && (specialtyCode ? hasProviderData : true);

    return (
        <div className={resolvedClassName}>
            <h4 className="asset-header">{title}</h4>
            <div className="map" style={mapStyle}>
                {shouldRenderMap && (
                    <BaseMap
                        style={mapStyle}
                        onMapRefReady={setMapRef}
                        layers={[]}
                        viewState={{
                            bounds: CONTINENTAL_US_BOUNDS,
                            fitBoundsOptions: {
                                padding: 20
                            }
                        }}
                        mapStyle={'mapbox://styles/mapbox/dark-v11'}
                        disableInteractions={true}
                        optimizeForPrint={true}
                    >
                        {showBoundaries && stateMapboxIds.length > 0 && (
                            <Source id="state-boundaries" type="vector" url="mapbox://mapbox.boundaries-adm1-v4" promoteId="mapbox_id">
                                <Layer {...{
                                    id: 'state-boundary-line',
                                    type: 'line' as const,
                                    'source-layer': 'boundaries_admin_1',
                                    filter: stateFilter,
                                    paint: {
                                        'line-color': '#000000',
                                        'line-width': 3,
                                        'line-opacity': 1
                                    }
                                }} />
                            </Source>
                        )}
                        {showBoundaries && countyMapboxIds.length > 0 && (
                            <Source id="county-boundaries" type="vector" url="mapbox://mapbox.boundaries-adm2-v4" promoteId="mapbox_id">
                                {countyFillPaint && (
                                    <Layer {...{
                                        id: 'county-boundary-fill',
                                        type: 'fill' as const,
                                        'source-layer': 'boundaries_admin_2',
                                        filter: countyFilter,
                                        paint: countyFillPaint
                                    }} />
                                )}
                                <Layer {...{
                                    id: 'county-boundary-line',
                                    type: 'line' as const,
                                    'source-layer': 'boundaries_admin_2',
                                    filter: countyFilter,
                                    paint: {
                                        'line-color': '#000000',
                                        'line-width': 0.5,
                                        'line-opacity': 1
                                    }
                                }} />
                            </Source>
                        )}
                        {providerGeoJson && providerFeatureCount > 0 && isMapPinLoaded && (
                            <Source id="provider-pins" type="geojson" data={providerGeoJson}>
                                <Layer {...{
                                    id: 'provider-pin-layer',
                                    type: 'symbol' as const,
                                    layout: {
                                        'icon-image': 'map-pin',
                                        'icon-size': 0.20,
                                        'icon-allow-overlap': true
                                    },
                                    paint: {
                                        'icon-color': PROVIDER_COLOR
                                    }
                                }} />
                            </Source>
                        )}
                    </BaseMap>
                )}
                {!!imgSrc && <img src={imgSrc} alt={title} id={id}/>}
                {!imgSrc && !hasError && <div className="map-loading">
                    <span>Loading...</span>
                </div>}
                {legend}
                {hasError && <div className="map-error">
                    <span>Map unavailable</span>
                </div>}
                <div className="map-attribution">
                    © Mapbox, © OpenStreetMap
                </div>
            </div>
            {subtitle && (
                <p>
                    <small>{subtitle}</small>
                </p>
            )}
        </div>
    );
});

export default MapContainer;